import React, {createContext, useContext, useEffect} from 'react';
import {useMemo} from 'react';
import {useCallback} from 'react';
import {useState} from 'react';
import Modal from '../affordance/Modal';
import ModalPending from '../affordance/ModalPending';
import ModalConfirm from '../affordance/ModalConfirm';
import ErrorBoundary from '../error/ErrorBoundary';
import Text from '../affordance/Text';
import Button from '../affordance/Button';
import Toast from '../affordance/Toast';
import Overlay from '../affordance/Overlay';
import Paper from '../affordance/Paper';
import {useHistory, useLocation} from 'react-router-dom';
import {useAnalytics} from './analytics/AnalyticsContext';
import FullscreenModal from '../affordance/FullscreenModal';
import usePrevious from '../util/usePrevious';

//
// Configs
type MessageConfig = {
    title: string;
    description: React.ReactNode;
    buttons?: (onClose: () => void) => React.ReactNode;
    maxWidth?: string;
};

type ConfirmConfig = {
    title: string;
    subtitle: React.ReactNode;
    yes: (onClose: () => void, isSafe: boolean) => React.ReactNode;
    no?: (onClose: () => void) => React.ReactNode;
    passphrase?: string;
    maxWidth?: string;
};

type CustomConfig = (onRequestClose: () => void) => React.ReactNode;
type CustomOptions = {
    alignItems?: string;
    maxWidth?: string;
    width?: string;
    /** Turn on if you want to prevent overflowing the modal overlay. You will need to setup your
     * own CSS to control possible overflow in your modal.custom implementation */
    disableOverflow?: boolean;
    onClose?: () => void;
    /** Set this to `true` only if you provide an `onClose` callback */
    preventDefaultClose?: boolean;
};

type ToastConfig = {
    type: 'success' | 'error';
    message: string;
    timer?: number;
};

type FullscreenOptions = {
    onClose?: () => void;
    /** Set this to `true` only if you provide an `onClose` callback */
    preventDefaultClose?: boolean;
};

//
// Context
type ModalContextData = {
    close: () => void;
    confirm: (config: ConfirmConfig) => void;
    custom: (config: CustomConfig, options?: CustomOptions) => void;
    fullscreen: (children: React.ReactNode, options?: FullscreenOptions) => void;
    message: (config: MessageConfig) => void;
    pending: (title: string, subtitle?: string) => void;
    toast: (config: ToastConfig) => void;
    toastError: (message?: string, options?: {timer?: number; error?: Error}) => void;
    toastSuccess: (message: string, timer?: number) => void;
};
const ModalContext = createContext<ModalContextData | undefined>(undefined);

type OverlayProps = Omit<
    React.ComponentProps<typeof Overlay>,
    'children' | 'isOpen' | 'onRequestClose'
> &
    CustomOptions;
//
// Provider
export function ModalProvider(props: {children: React.ReactNode}) {
    const [modal, setModal] = useState<React.ReactNode>(null);
    const [toast, setToast] = useState<React.ReactNode>(null);
    const onRequestModalClose = useCallback(() => setModal(null), []);
    const onRequestToastClose = useCallback(() => setToast(null), []);

    const analytics = useAnalytics();
    const history = useHistory();
    const location = useLocation();

    function setModalWithOverlay(content: React.ReactNode, overlayProps: OverlayProps) {
        setModal(
            <ErrorBoundary>
                <Overlay
                    isOpen={true}
                    showCross={false}
                    onRequestClose={() => {
                        overlayProps?.onClose?.();
                        !overlayProps?.preventDefaultClose && onRequestModalClose();
                    }}
                    {...overlayProps}
                    children={() => content}
                />
            </ErrorBoundary>
        );
    }

    function setFullscreenModal(content: React.ReactNode, options: FullscreenOptions) {
        setModal(
            <ErrorBoundary>
                <FullscreenModal
                    onRequestClose={() => {
                        options?.onClose?.();
                        !options?.preventDefaultClose && onRequestModalClose();
                    }}
                >
                    {content}
                </FullscreenModal>
            </ErrorBoundary>
        );
    }

    const modalContextData: ModalContextData = useMemo(() => {
        const toast = ({type, message, timer = 2000}: ToastConfig) => {
            setToast(
                <Toast
                    type={type}
                    timer={timer}
                    onRequestClose={onRequestToastClose}
                    children={message}
                />
            );
        };
        return {
            fullscreen: (content: React.ReactNode, options: FullscreenOptions) =>
                setFullscreenModal(content, options),
            close: onRequestModalClose,
            toast,
            toastSuccess: (message: string, timer?: number) =>
                toast({message, timer, type: 'success'}),
            toastError: (
                message = 'Oops, something went wrong!',
                options?: {
                    timer?: number;
                    error?: Error;
                }
            ) => {
                if (options?.error) analytics.trackError(options.error);
                toast({message, timer: options?.timer, type: 'error'});
            },
            custom: (fn: CustomConfig, config: CustomOptions = {}) => {
                setModalWithOverlay(
                    <Paper
                        borderRadius=".5rem"
                        maxHeight="calc(100vh - 4rem)"
                        mx="auto"
                        maxWidth={config.maxWidth}
                        width={config.width}
                        style={config.disableOverflow ? {overflow: 'hidden'} : undefined}
                    >
                        <ErrorBoundary>{fn(onRequestModalClose)}</ErrorBoundary>
                    </Paper>,
                    {
                        maxWidth: config.maxWidth,
                        alignItems: config.alignItems,
                        showCross: true,
                        noOverlayOverflow: config.disableOverflow,
                        onClose: config.onClose,
                        preventDefaultClose: config.preventDefaultClose
                    }
                );
            },
            message: (config: MessageConfig) =>
                setModalWithOverlay(
                    <Modal
                        title={config.title}
                        children={<Text textStyle="heading4">{config.description}</Text>}
                        buttons={config.buttons?.(onRequestModalClose)}
                        onRequestClose={onRequestModalClose}
                        maxWidth={config.maxWidth}
                    />,
                    {maxWidth: config.maxWidth || '24rem'}
                ),
            confirm: (config: ConfirmConfig) =>
                setModalWithOverlay(
                    <ModalConfirm
                        title={config.title}
                        passphrase={config.passphrase}
                        subtitle={config.subtitle}
                        yes={({isSafe}) => config.yes(onRequestModalClose, isSafe)}
                        no={
                            config.no?.(onRequestModalClose) ?? (
                                <Button secondary onClick={onRequestModalClose}>
                                    Cancel
                                </Button>
                            )
                        }
                    />,
                    {maxWidth: config.maxWidth ?? '24rem'}
                ),
            pending: (title: string, subtitle?: string) =>
                setModalWithOverlay(<ModalPending title={title} subtitle={subtitle} />, {
                    maxWidth: '20rem'
                })
        };
    }, []);

    const previousPathname = usePrevious(location.pathname);

    // closes any modal on route change
    useEffect(() => {
        return history.listen((locationListener) => {
            if (previousPathname !== locationListener.pathname) {
                modalContextData.close();
            }
        });
    }, [previousPathname]);

    // closes any modal on back button click
    useEffect(() => {
        const backButtonListener = () => {
            modalContextData.close();
        };

        window.addEventListener('popstate', backButtonListener);

        return () => {
            window.removeEventListener('popstate', backButtonListener);
        };
    }, [previousPathname]);

    return (
        <ModalContext.Provider value={modalContextData}>
            {modal}
            {toast}
            {props.children}
        </ModalContext.Provider>
    );
}

//
// Hook
export function useModal(): ModalContextData {
    const modal = useContext(ModalContext);
    if (!modal) throw new Error('ModalContext used before it exists');
    return modal;
}
